/**
 * Rhythmz — Scene: Light Beams v2
 * ──────────────────────────────────
 * 8 volumetric light cones radiating from a central point. High-opacity,
 * bright emissive, additive blending — actual laser beams.
 *
 * Audio: Bass → beam intensity + rotation. Beat → flash.
 */

import * as THREE from 'three/webgpu';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface BeamUniforms { uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N; uCol0: N; uCol1: N; uCol2: N; }

export function createLightBeams(u: BeamUniforms): SceneHandle {
  const group = new THREE.Group();

  // ── Beam cones: longer, thicker, visible ──
  const beams: THREE.Mesh[] = [];
  const beamColors: THREE.Color[] = [];

  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    // Long, thin cone
    const geo = new THREE.ConeGeometry(0.12, 3, 16);
    const mat = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      color: new THREE.Color().setHSL(i / 8, 1, 0.6),
    });
    const mesh = new THREE.Mesh(geo, mat);
    // Point outward — cone's axis is Z, rotate so it points radially
    mesh.rotation.x = Math.PI / 2;
    mesh.rotation.z = angle;
    group.add(mesh);
    beams.push(mesh);
    beamColors.push(new THREE.Color().setHSL(i / 8, 1, 0.6));

    // Also add a small sphere at the tip for the beam endpoint glow
    const tipGeo = new THREE.SphereGeometry(0.06, 6, 6);
    const tipMat = new THREE.MeshBasicNodeMaterial({
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      color: new THREE.Color().setHSL(i / 8, 1, 0.8),
    });
    const tip = new THREE.Mesh(tipGeo, tipMat);
    tip.position.set(Math.cos(angle) * 3, 0, Math.sin(angle) * 3);
    group.add(tip);
    beams.push(tip);
    beamColors.push(new THREE.Color().setHSL(i / 8, 1, 0.8));
  }

  // ── Center glow ──
  const coreGeo = new THREE.IcosahedronGeometry(0.3, 3);
  const coreMat = new THREE.MeshBasicNodeMaterial({
    color: 0xff44aa,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const core = new THREE.Mesh(coreGeo, coreMat);
  group.add(core);

  let t = 0;

  const update = (dt: number): void => {
    t += dt;
    const bass = u.uBass.value ?? 0;
    const beat = u.uBeat.value ?? 0;

    const hueOffset = t * 0.05;
    const beamIntensity = 0.4 + bass * 0.6 + beat * 0.8;

    // Update beams
    beams.forEach((m, i) => {
      const col = beamColors[i];
      const hue = (hueOffset + i * 0.125) % 1;
      col.setHSL(hue, 1, 0.5 + beat * 0.3);
      (m.material as THREE.MeshBasicNodeMaterial).color.copy(col);
      (m.material as THREE.MeshBasicNodeMaterial).opacity = Math.min(0.9, beamIntensity);

      // Sweep
      if (i % 2 === 0) {
        const beamIdx = i / 2;
        m.rotation.z = (beamIdx / 8) * Math.PI * 2 + Math.sin(t * 0.3 + beamIdx * 0.5) * 0.4 * (1 + bass);
      }
    });

    // Center core
    core.scale.setScalar(1 + beat * 1.5 + bass * 0.3);
    core.material.color.setHSL(hueOffset, 1, 0.6 + beat * 0.3);

    group.rotation.y = t * 0.15;
  };

  const dispose = (): void => {
    beams.forEach(m => { m.geometry.dispose(); (m.material as THREE.Material).dispose(); });
    coreGeo.dispose(); coreMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Light Beams' };
}